import { CONTACT_EMAIL, GAME_NAME, LISTDLE_URL } from '@geosport/shared';
import { useState, type ReactNode } from 'react';
import { useInstallPrompt } from '../lib/install';
import { Link } from '../lib/router';
import { SettingsSheet } from '../screens/SettingsSheet';
import { Button } from './Button';
import { Drawer } from './Drawer';

/** Page chrome for everything except the play screen. */
export function Shell({
  children,
  title,
  wide = false,
}: {
  children: ReactNode;
  title?: string;
  wide?: boolean;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const install = useInstallPrompt();
  return (
    <div
      className={`mx-auto flex min-h-dvh w-full flex-col px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] ${wide ? 'max-w-2xl' : 'max-w-lg'}`}
    >
      <header className="flex items-center justify-between py-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-white/80 hover:bg-white/10"
          >
            <svg
              aria-hidden="true"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <Link to="/" className="flex items-center gap-2 text-lg font-bold">
            <img src="/favicon.svg" alt="" className="h-7 w-7" />
            {GAME_NAME}
          </Link>
        </div>
        <nav className="flex items-center gap-1 text-sm">
          {/* The install button only becomes available once Chromium fires `beforeinstallprompt`,
              seconds after first paint. Its slot is always laid out (invisible until then) so the
              gear never jumps sideways: that jump alone was a 0.12 layout shift. */}
          <div className="hidden sm:block">
            <Button
              variant="secondary"
              size="sm"
              className={install.canInstall ? '' : 'invisible'}
              onClick={() => void install.prompt()}
            >
              Get the app
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            aria-label="Settings"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-white/80 hover:bg-white/10"
          >
            <svg
              aria-hidden="true"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
            </svg>
          </button>
        </nav>
      </header>
      {title && <h1 className="mb-4 text-2xl font-bold">{title}</h1>}
      <main className="flex-1">{children}</main>
      <footer className="mt-10 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-white/40">
        <Link to="/how-to-play">How it works</Link>
        <a href="/faq">FAQ</a>
        <a href="/puzzles">Archive</a>
        <a href="/sports">Sports quizzes</a>
        <a href="/press">Press</a>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
        <a href={`mailto:${CONTACT_EMAIL}`}>Contact</a>
        <button type="button" onClick={() => setSettingsOpen(true)} className="hover:text-white/70">
          Delete my data
        </button>
        <a
          href={LISTDLE_URL}
          target="_blank"
          rel="noopener"
          className="mt-3 basis-full text-center"
        >
          <img
            src="/badges/listdle.svg"
            alt="Rate Geosportle on Listdle"
            width={148}
            height={24}
            className="inline-block"
          />
        </a>
      </footer>
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} />
      <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
